import { IContest, ITier, SubmissionCategory } from '../types/global/helper';

export type ContestSubmissionMode = 'tier' | 'regular';

export interface ContestTierFeeDraft {
    label: string;
    fee: number;
    currency?: string;
    starts_at?: string | null;
    ends_at?: string | null;
    timezone?: string | null;
    is_active?: boolean;
    _id?: string;
}

export function resolveCategorySubmissionMode(
    category?: SubmissionCategory | null,
    contest?: Pick<IContest, 'submission_mode'> | null,
): ContestSubmissionMode {
    if (category?.with_tiered_pricing === true) return 'tier';
    if (category?.with_tiered_pricing === false) return 'regular';
    if (contest?.submission_mode === 'tier' || contest?.submission_mode === 'regular') {
        return contest.submission_mode;
    }
    if (category?.tier_fee && category.tier_fee.length > 0) return 'tier';
    return 'regular';
}

export function resolveContestSubmissionMode(contest?: IContest | null): ContestSubmissionMode {
    if (contest?.submission_mode === 'tier' || contest?.submission_mode === 'regular') {
        return contest.submission_mode;
    }
    const categories = contest?.categories ?? [];
    if (categories.some(c => c.with_tiered_pricing === true || (c.tier_fee && c.tier_fee.length > 0))) {
        return 'tier';
    }
    return 'regular';
}

/** Parse fee values that may be numbers, major units, or currency display strings ("$25.00"). */
export function parseFeeAmount(value: unknown): number {
    if (value == null || value === '') return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const n = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
    return Number.isNaN(n) ? 0 : n;
}

export function tierFeeMajorUnits(tier: Pick<ITier, 'fee' | 'bare_amount'>): number {
    if (tier.bare_amount != null && !Number.isNaN(Number(tier.bare_amount))) {
        return Number(tier.bare_amount) / 100;
    }
    return parseFeeAmount(tier.fee);
}

export function flatFeeMajorUnits(category?: SubmissionCategory | null): number {
    if (!category) return 0;
    if (category.bare_amount != null && !Number.isNaN(Number(category.bare_amount))) {
        return Number(category.bare_amount) / 100;
    }
    return parseFeeAmount(category.fee);
}

/**
 * Mirrors backend contestSubmissionPaymentService.categoryRequiresSubmissionPayment.
 * Category-level: true if flat fee or any tier costs money.
 * Prefer submissionRequiresPayment when a chosen_tier is known.
 */
export function categoryRequiresSubmissionPayment(category?: SubmissionCategory | null): boolean {
    if (!category) return false;
    if (category.with_fee === true) return true;
    if (category.bare_amount != null && Number(category.bare_amount) > 0) return true;
    if (parseFeeAmount(category.fee) > 0) return true;
    if (
        Array.isArray(category.tier_fee) &&
        category.tier_fee.some(t => (t?.bare_amount != null && Number(t.bare_amount) > 0) || parseFeeAmount(t?.fee) > 0)
    ) {
        return true;
    }
    return false;
}

function resolveChosenTierFromCategory(
    category?: SubmissionCategory | null,
    chosenTier?: { label?: string; fee?: number; bare_amount?: number; _id?: string } | null,
): ITier | null {
    if (!category || !chosenTier) return null;
    const tiers = category.tier_fee ?? [];
    if (!tiers.length) return null;
    if (chosenTier._id) {
        const byId = tiers.find(t => t._id === chosenTier._id);
        if (byId) return byId;
    }
    const label = chosenTier.label != null ? String(chosenTier.label) : '';
    if (label) {
        const byLabel = tiers.find(t => String(t.label || '') === label);
        if (byLabel) return byLabel;
    }
    return null;
}

/**
 * Mirrors backend submissionRequiresPayment — Free tier on a mixed category skips checkout.
 */
export function submissionRequiresPayment(
    category?: SubmissionCategory | null,
    chosenTier?: { label?: string; fee?: number; bare_amount?: number; _id?: string } | null,
): boolean {
    if (!category) return false;
    const tiers = category.tier_fee ?? [];
    const isTiered =
        category.with_tiered_pricing === true || (category.with_tiered_pricing !== false && tiers.length > 0);
    if (isTiered && tiers.length > 0) {
        const matched = resolveChosenTierFromCategory(category, chosenTier);
        if (matched) {
            if (matched.bare_amount != null && !Number.isNaN(Number(matched.bare_amount))) {
                return Number(matched.bare_amount) > 0;
            }
            return parseFeeAmount(matched.fee) > 0;
        }
        return categoryRequiresSubmissionPayment(category);
    }
    return categoryRequiresSubmissionPayment(category);
}

/** Resolve a contest category by id or name for payment / submit gates. */
export function findContestSubmissionCategory(
    contest?: Pick<IContest, 'categories'> | null,
    categoryHint?: string | SubmissionCategory | null,
): SubmissionCategory | undefined {
    const categories = contest?.categories ?? [];
    if (!categories.length) return undefined;
    if (categoryHint && typeof categoryHint === 'object') {
        return categories.find(c => c._id === categoryHint._id) ?? categoryHint;
    }
    if (typeof categoryHint === 'string' && categoryHint) {
        return categories.find(c => c._id === categoryHint || c.category_name === categoryHint);
    }
    return categories.length === 1 ? categories[0] : undefined;
}

/** Default tier for to-regular: sole tier, else regular_default_tier_id match, else first. */
export function pickDefaultTierId(category?: SubmissionCategory | null): string | null {
    const tiers = category?.tier_fee ?? [];
    if (!tiers.length) return null;
    if (tiers.length === 1) return tiers[0]._id || null;
    if (category?.regular_default_tier_id) {
        const match = tiers.find(t => t._id === category.regular_default_tier_id);
        if (match?._id) return match._id;
    }
    return tiers[0]._id || null;
}

export function seedTiersFromRegularCategory(category?: SubmissionCategory | null): ContestTierFeeDraft[] {
    const fee = flatFeeMajorUnits(category);
    const label = category?.regular_default_tier_label || 'Standard';
    return [
        {
            label,
            fee,
            currency: category?.currency || 'USD',
            starts_at: null,
            ends_at: null,
            is_active: true,
        },
    ];
}

export function validateTierWindows(tiers: ContestTierFeeDraft[]): string | null {
    for (const tier of tiers) {
        if (tier.starts_at && tier.ends_at) {
            const start = Date.parse(tier.starts_at);
            const end = Date.parse(tier.ends_at);
            if (!Number.isNaN(start) && !Number.isNaN(end) && start >= end) {
                return `"${tier.label || 'Tier'}" must start before it ends.`;
            }
        }
    }
    const windows = tiers
        .map((t, index) => ({
            index,
            label: t.label || `Tier ${index + 1}`,
            start: t.starts_at ? Date.parse(t.starts_at) : Number.NEGATIVE_INFINITY,
            end: t.ends_at ? Date.parse(t.ends_at) : Number.POSITIVE_INFINITY,
        }))
        .filter(w => !Number.isNaN(w.start) && !Number.isNaN(w.end));

    for (let i = 0; i < windows.length; i += 1) {
        for (let j = i + 1; j < windows.length; j += 1) {
            const a = windows[i];
            const b = windows[j];
            if (a.start < b.end && b.start < a.end) {
                return `"${a.label}" and "${b.label}" date windows overlap.`;
            }
        }
    }
    return null;
}

export function buildToRegularPayload(args: {
    categoryId?: string;
    defaultTierId: string;
    force?: boolean;
    keepInactiveTiers?: boolean;
}) {
    return {
        ...(args.categoryId ? { category_id: args.categoryId } : {}),
        default_tier_id: args.defaultTierId,
        force: Boolean(args.force),
        keep_inactive_tiers: args.keepInactiveTiers !== false,
    };
}

export function buildToTierPayload(args: {
    categoryId?: string;
    tiers: ContestTierFeeDraft[];
    seedFromRegularFee?: boolean;
    mapRegularFeeToLabel?: string;
}) {
    return {
        ...(args.categoryId ? { category_id: args.categoryId } : {}),
        tier_fee: args.tiers.map(t => ({
            label: t.label.trim(),
            fee: t.fee,
            currency: t.currency || 'USD',
            starts_at: t.starts_at ?? null,
            ends_at: t.ends_at ?? null,
            ...(t.timezone ? { timezone: t.timezone } : {}),
            ...(t.is_active === false ? { is_active: false } : {}),
            ...(t._id ? { _id: t._id } : {}),
        })),
        seed_from_regular_fee: Boolean(args.seedFromRegularFee),
        ...(args.mapRegularFeeToLabel ? { map_regular_fee_to_label: args.mapRegularFeeToLabel } : {}),
    };
}
